import { useEffect, useRef } from 'react';

type TurnstileWidgetProps = {
    className?: string;
    onToken: (token: string) => void;
    resetNonce?: number;
    siteKey: string;
};

/**
 * Reusable Cloudflare Turnstile widget. The Turnstile script is loaded globally
 * by Default.aspx, but it may not be ready when this mounts, so we poll for it.
 *
 * `onToken` fires with the solved token (and with '' when the token expires or
 * errors). Bump `resetNonce` to force the widget back to its unsolved state
 * after a failed submit (Turnstile tokens are single-use).
 */
export const TurnstileWidget = ({ className, onToken, resetNonce = 0, siteKey }: TurnstileWidgetProps) => {

    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    useEffect(() => {

        if (!siteKey) return undefined;

        let intervalId: number | undefined;

        const renderWidget = () => {

            const turnstile = (window as any).turnstile;
            if (!turnstile || !containerRef.current || widgetIdRef.current !== null) return;

            widgetIdRef.current = turnstile.render(containerRef.current, {
                callback: (token: string) => onToken(token),
                'error-callback': () => onToken(''),
                'expired-callback': () => onToken(''),
                sitekey: siteKey,
                theme: 'light'
            });

        };

        if ((window as any).turnstile) {

            renderWidget();

        } else {

            intervalId = window.setInterval(() => {

                if ((window as any).turnstile) {

                    window.clearInterval(intervalId);
                    intervalId = undefined;
                    renderWidget();

                }

            }, 200);

        }

        return () => {

            if (intervalId !== undefined) window.clearInterval(intervalId);

            const turnstile = (window as any).turnstile;
            if (turnstile && widgetIdRef.current !== null) {

                try {

                    turnstile.remove(widgetIdRef.current);

                } catch {

                    // Widget already gone; nothing to clean up.
                }
                widgetIdRef.current = null;

            }

        };

    }, [siteKey]);

    useEffect(() => {

        if (resetNonce === 0) return;

        const turnstile = (window as any).turnstile;
        if (turnstile && widgetIdRef.current !== null) {

            try {

                turnstile.reset(widgetIdRef.current);

            } catch {

                // Reset is best-effort.
            }
            onToken('');

        }

    }, [resetNonce]);

    return <div className={className} ref={containerRef} />;

};
